// terminVorgang.ts — pure Helfer für „Vorgang aus Termin" (Termine-SP8, A17):
// Kontakt-Ableitung aus den Teilnehmer-E-Mails eines Termins.
//
// Bewusst OHNE React/DOM (vitest-testbar, Muster peek.ts/dedupe.ts):
// - interne KMVS-Adressen (listAppUsers + eigenes Postfach) ausfiltern —
//   Kollegen sind keine Kunden-Kandidaten (SP8-Testpunkt),
// - externe Teilnehmer-/Organisator-Adressen normalisieren, deduplizieren
//   und deckeln (kein Request-Sturm bei Groß-Meetings),
// - exakte Treffer der Empfänger-Kontaktsuche bestimmen: `GET
//   /contact/email-recipients` sucht als SUBSTRING in Name UND E-Mail — erst
//   der Exakt-Abgleich hier macht daraus einen Kontakt-Resolve (Plan 3.9,
//   „Optional-Backendlos": kein eigener /contact/by-email-Endpoint nötig),
// - Kandidaten über alle Adressen je contactId dedupen (ein Kontakt mit
//   mehreren E-Mails erscheint in email-recipients mehrfach).
//
// Die Dialog-UI (Auswahl/Fallback + Auto-Bind) lebt in TerminVorgangDialog.tsx.

import type { EmailRecipientHit } from "../../services/graphMailService";

/** Teilnehmer-Quelle eines Termins (Peek-Event bzw. Termin-Fenster-Detail). */
export interface TerminTeilnehmerQuelle {
    attendees?:
        | { address: string; name?: string | null; type?: string | null }[]
        | null;
    organizer?: { address: string; name?: string | null } | null;
}

/** Ein aufgelöster Kontakt-Kandidat (dedupet je contactId). */
export interface KontaktKandidat {
    contactId: number;
    displayName: string;
    email: string;
    companyName?: string;
}

/** Deckel für die E-Mail-Auflösung (ein Request je Adresse). */
export const MAX_RESOLVE_ADRESSEN = 8;

const norm = (s: string | null | undefined): string => (s ?? "").trim().toLowerCase();

/**
 * Interne Adressen: alle App-User-E-Mails (listAppUsers) + das Postfach des
 * Termins selbst (geteilte Postfächer wie zentrale@ sind keine App-User).
 */
export function interneAdressSet(
    users: { email?: string | null }[],
    eigeneMailbox?: string | null,
): Set<string> {
    const set = new Set<string>();
    for (const u of users) {
        const e = norm(u.email);
        if (e) set.add(e);
    }
    const mb = norm(eigeneMailbox);
    if (mb) set.add(mb);
    return set;
}

/**
 * Externe Kandidaten-Adressen eines Termins: Teilnehmer + Organisator (ein
 * Kunde kann selbst eingeladen haben), OHNE Räume (`type === 'resource'`),
 * OHNE interne Adressen, dedupliziert und auf `max` gedeckelt.
 */
export function externeTeilnehmerAdressen(
    quelle: TerminTeilnehmerQuelle,
    intern: Set<string>,
    max: number = MAX_RESOLVE_ADRESSEN,
): string[] {
    const out: string[] = [];
    const gesehen = new Set<string>();
    const eintraege: { address?: string | null; type?: string | null }[] = [
        ...(quelle.attendees ?? []),
        ...(quelle.organizer ? [{ address: quelle.organizer.address }] : []),
    ];
    for (const a of eintraege) {
        if ((a.type ?? "") === "resource") continue; // Räume sind keine Kunden
        const email = norm(a.address);
        if (!email || !email.includes("@")) continue;
        if (intern.has(email) || gesehen.has(email)) continue;
        gesehen.add(email);
        out.push(email);
        if (out.length >= max) break;
    }
    return out;
}

/**
 * Exakte Treffer für EINE Adresse: die Empfänger-Suche liefert auch
 * Substring-Treffer (Name enthält den Suchstring etc.) — als Kontakt-Resolve
 * zählt nur die identische E-Mail (case-insensitiv).
 */
export function exakteTreffer(
    email: string,
    hits: EmailRecipientHit[],
): EmailRecipientHit[] {
    const e = norm(email);
    return hits.filter((h) => norm(h.email) === e);
}

/**
 * Trefferpaare (Adresse → Such-Hits) zu dedupliziertem Kandidaten-Ergebnis:
 * - `kandidaten`: exakte Kontakt-Treffer, je contactId genau einmal,
 * - `unbekannt`: Adressen ohne exakten Treffer (→ Fallback „Kontakt anlegen").
 */
export function kandidatenAusTreffern(
    paare: { email: string; hits: EmailRecipientHit[] }[],
): { kandidaten: KontaktKandidat[]; unbekannt: string[] } {
    const kandidaten: KontaktKandidat[] = [];
    const gesehen = new Set<number>();
    const unbekannt: string[] = [];
    for (const { email, hits } of paare) {
        const exakt = exakteTreffer(email, hits);
        if (exakt.length === 0) {
            unbekannt.push(norm(email));
            continue;
        }
        for (const h of exakt) {
            if (gesehen.has(h.contactId)) continue;
            gesehen.add(h.contactId);
            kandidaten.push({
                contactId: h.contactId,
                displayName: h.displayName || h.email,
                email: h.email,
                companyName: h.companyName,
            });
        }
    }
    return { kandidaten, unbekannt };
}
